import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';

const TEAL = '#00a99d';
const NAVY = '#1a1a2e';

const PrivacyPolicy = () => (
  <div style={{ minHeight: '100vh', background: '#fff', fontFamily: "'Inter', system-ui, sans-serif", color: NAVY }}>
    {/* Nav */}
    <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: '#fff', boxShadow: '0 1px 12px rgba(0,0,0,0.05)' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Building2 style={{ width: 28, height: 28, color: TEAL }} />
          <span style={{ fontSize: 22, fontWeight: 700, color: NAVY }}>Realty <span style={{ color: TEAL }}>JAG</span></span>
        </Link>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#595959', textDecoration: 'none' }}>
          <ArrowLeft style={{ width: 16, height: 16 }} /> Back to Home
        </Link>
      </div>
    </nav>

    <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 80px' }}>
      <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 8 }}>Privacy Policy</h1>
      <p style={{ color: '#595959', marginBottom: 32 }}>Last updated: March 27, 2026</p>

      <div style={{ lineHeight: 1.8, color: '#333', fontSize: 15 }}>
        <p>Realty JAG LLC ("we", "us", or "our") operates the Realty JAG platform (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>1. Information We Collect</h2>
        <p><strong>Personal Information:</strong> When you register for an account, we collect your name, email address, phone number, and payment information (processed securely through Stripe).</p>
        <p><strong>Property Information:</strong> Landlords provide property addresses, unit details, lease terms, and tenant information to use the platform.</p>
        <p><strong>Usage Data:</strong> We automatically collect information about how you access and use the Service, including IP addresses, browser type, pages viewed, and timestamps.</p>
        <p><strong>Uploaded Files:</strong> Documents and images uploaded to the platform (lease agreements, property photos, maintenance photos) are stored securely.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul style={{ paddingLeft: 24 }}>
          <li>Provide, maintain, and improve the Service</li>
          <li>Process transactions and send related notifications</li>
          <li>Send email notifications about payments, maintenance requests, and lease events</li>
          <li>Respond to your inquiries and provide customer support</li>
          <li>Monitor and analyze usage patterns and trends</li>
          <li>Detect, prevent, and address technical issues or fraud</li>
        </ul>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>3. Information Sharing</h2>
        <p>We do not sell your personal information. We may share your information with:</p>
        <ul style={{ paddingLeft: 24 }}>
          <li><strong>Service Providers:</strong> Stripe (payment processing), Firebase (authentication), Resend (email delivery), and cloud hosting providers</li>
          <li><strong>Landlord-Tenant Relationship:</strong> Information necessary for the landlord-tenant relationship (e.g., contact details, payment status, maintenance requests)</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
        </ul>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>4. Data Security</h2>
        <p>We implement industry-standard security measures to protect your information, including encryption in transit (TLS/SSL), secure cloud storage, and regular security audits. Payment information is handled exclusively by Stripe and never stored on our servers.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>5. Data Retention</h2>
        <p>We retain your information for as long as your account is active or as needed to provide services. Upon account deletion, we will remove your personal data within 30 days, except where retention is required by law.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>6. Your Rights</h2>
        <p>You have the right to:</p>
        <ul style={{ paddingLeft: 24 }}>
          <li>Access and receive a copy of your personal data</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Opt out of marketing communications</li>
          <li>Export your data in a portable format</li>
        </ul>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>7. Cookies</h2>
        <p>We use essential cookies to maintain your session and authentication state. We do not use third-party advertising cookies.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>8. Children's Privacy</h2>
        <p>Our Service is not directed to children under 18. We do not knowingly collect information from children.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>9. Changes to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.</p>

        <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginTop: 32, marginBottom: 12 }}>10. Contact Us</h2>
        <p>If you have questions about this Privacy Policy, please contact us:</p>
        <div style={{ background: '#f7f8fa', padding: 24, borderRadius: 12, marginTop: 12, border: '1px solid #e5e7eb' }}>
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Realty JAG LLC</p>
          <p style={{ margin: '0 0 4px' }}>350 N Federal Hwy, 715S</p>
          <p style={{ margin: '0 0 4px' }}>Boynton Beach, FL 33435</p>
          <p style={{ margin: '0 0 4px' }}>Email: <a href="mailto:contact@realtyjag.com" style={{ color: TEAL }}>contact@realtyjag.com</a></p>
        </div>
      </div>
    </div>

    {/* Footer */}
    <footer style={{ background: NAVY, padding: '24px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
          &copy; {new Date().getFullYear()} Realty JAG LLC. All rights reserved.
        </p>
      </div>
    </footer>
  </div>
);

export default PrivacyPolicy;
